import { gql, useSuspenseQuery } from '@apollo/client';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { DayOfWeekNames, dayOfWeekToIndex } from '../../types/datetimes';

// Computes the current Medicare week date range using the current date and the
// provided start day of the week.
export function getCurrentMedicareWeekDateRange(startDay: DayOfWeekNames): {
    start: moment.Moment;
    end: moment.Moment;
} {
    const today = moment();
    const currentWeekday = today.day();
    const startDayIndex = dayOfWeekToIndex(startDay);

    const daysToSubtract = (currentWeekday - startDayIndex + 7) % 7;
    const startOfWeek = today.subtract(daysToSubtract, 'day').startOf('day');
    const endOfWeek = startOfWeek.clone().add(6, 'day').endOf('day');
    return {
        start: startOfWeek,
        end: endOfWeek,
    };
}

// Computes the next Medicare week date range using the current date and the
// provided start day of the week.
export function getNextMedicareWeekDateRange(startDay: DayOfWeekNames): {
    start: moment.Moment;
    end: moment.Moment;
} {
    const currentWeek = getCurrentMedicareWeekDateRange(startDay);
    const nextWeek = currentWeek.start.clone().add(7, 'day');
    return {
        start: nextWeek,
        end: nextWeek.clone().add(6, 'day'),
    };
}

export const useMedicareWeekQuery = () => {
    const { data } = useSuspenseQuery<{
        currentOrganization: {
            id: string;
            medicareWeekStartDay: DayOfWeekNames;
            medicareWeekDays: DayOfWeekNames[];
        };
    }>(gql`
        query CurrentOrganizationMedicareWeek {
            currentOrganization {
                id
                medicareWeekStartDay
                medicareWeekDays
            }
        }
    `);

    return data.currentOrganization;
};

export const useMedicareWeeks = () => {
    const { medicareWeekStartDay, medicareWeekDays } = useMedicareWeekQuery();
    const currentMedicareWeek = getCurrentMedicareWeekDateRange(medicareWeekStartDay);
    const nextMedicareWeek = getNextMedicareWeekDateRange(medicareWeekStartDay);

    return {
        currentMedicareWeek,
        nextMedicareWeek,
        medicareWeekDays,
    };
};

// Hook exposing the current Medicare week date range, updating every hour in case
// the range changes while the page is open.
export function useCurrentMedicareWeekDateRange(): {
    start: moment.Moment;
    end: moment.Moment;
} {
    const { medicareWeekStartDay } = useMedicareWeekQuery();
    const [currentMedicareWeek, setCurrentMedicareWeek] = useState<{
        start: moment.Moment;
        end: moment.Moment;
    }>(getCurrentMedicareWeekDateRange(medicareWeekStartDay));

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentMedicareWeek(getCurrentMedicareWeekDateRange(medicareWeekStartDay));
        }, 1000 * 60 * 60);
        return () => clearInterval(interval);
    }, [medicareWeekStartDay]);

    return currentMedicareWeek;
}

// Hook exposing the next Medicare week date range, updating every hour in case
// the range changes while the page is open.
export function useNextMedicareWeekDateRange(): {
    start: moment.Moment;
    end: moment.Moment;
} {
    const { medicareWeekStartDay } = useMedicareWeekQuery();
    const [nextMedicareWeek, setNextMedicareWeek] = useState<{
        start: moment.Moment;
        end: moment.Moment;
    }>(getNextMedicareWeekDateRange(medicareWeekStartDay));

    useEffect(() => {
        const interval = setInterval(() => {
            setNextMedicareWeek(getNextMedicareWeekDateRange(medicareWeekStartDay));
        }, 1000 * 60 * 60);
        return () => clearInterval(interval);
    }, [medicareWeekStartDay]);

    return nextMedicareWeek;
}
